import { useEffect } from "react";
import { flash } from "../../web/lib/flash";
import type { TaxRate } from "../../shared/money";
import { ChoiceField, TextField } from "../../web/components/fields";
import { ErrorNotice, Notice, Panel, Spinner } from "../../web/components/ui";
import { can, usePriceList, usePriceListSaver, whyNot } from "./data";
import { type PriceListData, shortNameTaken } from "./meta";
import { JobTimeSettings } from "./JobTimeSettings";
import { TagsPanel } from "./TagsPanel";

/*
 * Tax, whatever the business's rules (its ServiceM8 tax rates): with one rate that charges tax (e.g. GST, beside GST-free), it's
 * called by name; with several, "tax"; with none, there's no choice to make.
 */

/** The business's rates that charge tax (above 0%). */
const taxingRates = (rates: readonly TaxRate[]) => rates.filter((r) => r.percent > 0);

/** What its tax is called: its one taxing rate's name (e.g. "GST"), else "tax". */
export const taxWordOf = (rates: readonly TaxRate[]) => {
    const taxing = taxingRates(rates);
    return taxing.length === 1 ? (taxing[0]?.name ?? "tax") : "tax";
};

/**
 * A short name to suggest for a long ServiceM8 tax rate name: what's before " on " ("GST on
 * Income": "GST"), else the initials of a name of three words or more ("Goods and Services Tax":
 * "GST", small words like "and" left out), else none.
 */
function suggestShortName(name: string) {
    const at = name.toLowerCase().indexOf(" on ");
    if (at > 0) return name.slice(0, at).trim();
    const words = name.trim().split(" ").filter(Boolean);
    // Already has a short form in it ("GST Free Income"): initials would only confuse ("GFI").
    if (words.length < 3 || words.some((w) => w.length > 1 && w === w.toUpperCase())) return "";
    return words
        .filter((w) => w[0] === w[0]?.toUpperCase())
        .map((w) => w[0])
        .join("");
}

/**
 * Short names for the business's ServiceM8 tax rates, shown in their place across the Price List
 * ("GST" for "GST on Income"); blank, its full name. Only kept here: ServiceM8's names (and the
 * accounting software's link to them) never change. Nothing without tax rates.
 */
function TaxRateNames({ data, disabled }: Readonly<{ data: PriceListData; disabled: boolean }>) {
    const saver = usePriceListSaver();
    const names = data.settings.taxShortNames;
    if (!data.taxRates.length) return null;
    // An example from its own rates ("GST on Income" as "GST"), if one has a long name to shorten.
    const example = data.taxRates.map((r) => ({ full: r.fullName ?? r.name, short: suggestShortName(r.fullName ?? r.name) })).find((e) => e.short);
    // Only this rate's (the server keeps the rest), so quick edits to two rates can't undo each other; blank: its full name again.
    const save = (uuid: string, value: string) => saver.saveSettings({ taxShortNames: { [uuid]: value } });
    return (
        <Panel title="Tax rates" id="tax-rates">
            <p className="m-0 mb-3 text-sm text-muted">
                Give a long tax rate name a short one to show across the Price List{example ? <>, e.g. &quot;{example.full}&quot; as &quot;{example.short}&quot;</> : null}. Leave it blank to use the
                full name. Only used here: never sent to ServiceM8.
            </p>
            {/* A small table: each rate's name and percentage as ServiceM8 has them, and its short name. */}
            <div className="max-w-xl">
                <div className="grid grid-cols-[minmax(0,1fr)_4rem_minmax(0,1fr)] gap-3 border-b border-line pb-1.5 text-xs text-muted">
                    <span>Name in ServiceM8</span>
                    <span>Rate</span>
                    <span>Short name</span>
                </div>
                {data.taxRates.map((r) => {
                    const full = r.fullName ?? r.name;
                    return (
                        <div key={r.uuid} className="grid grid-cols-[minmax(0,1fr)_4rem_minmax(0,1fr)] items-center gap-3 border-b border-line py-1.5 last:border-b-0">
                            <span className="truncate text-sm" title={full}>
                                {full}
                            </span>
                            <span className="text-sm text-muted tabular-nums">{r.percent}%</span>
                            <TextField
                                hideLabel
                                label={`Short name for ${full}`}
                                value={names[r.uuid] ?? ""}
                                placeholder={suggestShortName(full) ? `e.g. ${suggestShortName(full)}` : "Not set"}
                                disabled={disabled}
                                validate={(v) => {
                                    if (v.trim().length > 20) return "At most 20 characters";
                                    const taken = shortNameTaken(data.taxRates, names, r.uuid, v);
                                    return taken ? `${taken} reads as that already` : null;
                                }}
                                onSave={(v) => save(r.uuid, v.trim())}
                            />
                        </div>
                    );
                })}
            </div>
        </Panel>
    );
}

/**
 * A settings page's panels, linked to by their ids (Stats' "Price List settings" link goes to #job-time): opened scrolled to it,
 * and flashed, so the eye lands on it (as a warning's Show does): each time a link brings you here, the page already showing too
 * (a link's # said: RecordWindow.tsx).
 */
export function usePanelLinks() {
    useEffect(() => {
        const show = () => {
            const hash = globalThis.location.hash.slice(1);
            const panel = hash ? document.getElementById(hash) : null;
            if (!panel) return;
            panel.scrollIntoView({ block: "start", behavior: "smooth" });
            flash(panel, "is-flashed");
        };
        show();
        globalThis.addEventListener("hashchange", show);
        return () => globalThis.removeEventListener("hashchange", show);
    }, []);
}

/**
 * The Price List's settings, Settings › Price List (pages/settings/Settings.tsx; the gears on the Price List's and the Catalogue's
 * rows in the sidebar come here): selling (tax rates' short names, the minimum markup), buying (tax on purchases, supplier price
 * checks: the Catalogue's), tags (items' and products', one list) and job time. Seldom changed, so out of the way of the lists.
 * Anyone who can use the Price List sees them; whoever may change settings changes them (one settings row).
 */
export default function PriceListSettings() {
    const { data, error, isLoading } = usePriceList();
    if (isLoading) return <Spinner />;
    if (!data) return <ErrorNotice error={error ?? new Error("The Price List's settings couldn't be loaded.")} />;
    return <SettingsPanels data={data} />;
}

function SettingsPanels({ data }: Readonly<{ data: PriceListData }>) {
    const saver = usePriceListSaver();
    const s = data.settings;
    const disabled = !can(data, "settings");
    // Its tax by name, or "tax" (taxWordOf).
    const taxWord = taxWordOf(data.taxRates);
    usePanelLinks();

    return (
        <>
            {whyNot(data, "settings") ? (
                <div className="mb-4">
                    <Notice>{whyNot(data, "settings")}</Notice>
                </div>
            ) : null}
            {/* Every panel on the same two columns: a field is one, so nothing stretches across the page. */}
            <TaxRateNames data={data} disabled={disabled} />
            <Panel title="Profit" id="profit">
                <div className="grid gap-4 sm:grid-cols-2">
                    <TextField
                        label="Minimum markup"
                        value={String(s.minMarkup)}
                        suffix="%"
                        inputMode="decimal"
                        disabled={disabled}
                        validate={(v) => (Number.isFinite(Number(v)) && Number(v) >= 0 ? null : "Enter a percentage")}
                        hint="Items with a markup on their cost (before tax) under this are flagged. 0 flags only items that make nothing."
                        onSave={(v) => saver.saveSettings({ minMarkup: Number(v) })}
                    />
                </div>
            </Panel>
            <Panel title="Tax on purchases" id="tax-on-purchases">
                <div className="grid gap-4 sm:grid-cols-2">
                    <ChoiceField
                        label={`${taxWord === "tax" ? "Tax" : taxWord} you pay suppliers`}
                        value={s.purchaseTaxClaimed ? "claimed" : "cost"}
                        disabled={disabled}
                        options={[
                            { value: "claimed", label: "Claimed back", hint: "You claim it back, so it isn't part of your costs. Not sure? Ask your accountant." },
                            { value: "cost", label: "A real cost", hint: "You can't claim it back, so it's part of your costs. Not sure? Ask your accountant." },
                        ]}
                        onSave={(v) => saver.saveSettings({ purchaseTaxClaimed: v === "claimed" })}
                    />
                </div>
                {/* Costs never change by themselves (supplierPrices.ts costUpdates). */}
                <p className="m-0 mt-3 text-xs text-muted">
                    Changing this changes what supplier prices really cost. Items whose cost follows one then show &quot;Cost to update&quot; until they&apos;re updated.
                </p>
            </Panel>
            <Panel title="Supplier prices" id="supplier-prices">
                <div className="grid gap-4 sm:grid-cols-2">
                    <TextField
                        label="Check due after"
                        value={String(s.recheckDays)}
                        suffix="days"
                        inputMode="numeric"
                        disabled={disabled}
                        validate={(v) => (Number.isInteger(Number(v)) && Number(v) >= 1 ? null : "Enter a number of days")}
                        hint="Prices not checked for this long show as Check due (amber)."
                        onSave={(v) => saver.saveSettings({ recheckDays: Number(v) })}
                    />
                    <TextField
                        label="Out of date after"
                        value={String(s.outdatedDays)}
                        suffix="days"
                        inputMode="numeric"
                        disabled={disabled}
                        validate={(v) => (Number.isInteger(Number(v)) && Number(v) > s.recheckDays ? null : "Enter more days than Check due")}
                        hint="...and after this long, Out of date (red)."
                        onSave={(v) => saver.saveSettings({ outdatedDays: Number(v) })}
                    />
                </div>
            </Panel>
            <TagsPanel data={data} disabled={disabled} />
            <JobTimeSettings data={data} />
        </>
    );
}
